import { CheckCircle, Clock, ListChecks, Play, SkipForward, X } from "@phosphor-icons/react";
import type { FeaturePalette, Result, ScopeNode, SearchProvider, Verb } from "@/shared/components/command-palette/types";
import { getAvailableTasks, getOverdueTasks, getTasks } from "./api/tasks";

const TASKS_PARENT: ScopeNode = { id: "tasks", parentId: null, title: "Tasks" };

const scopes: ScopeNode[] = [
  { id: "tasks.rules", parentId: "tasks", title: "Rules", icon: Clock, href: "/tasks/rules", keywords: ["recurring", "schedule"] },
  {
    id: "tasks.available",
    parentId: "tasks",
    title: "Available now",
    icon: Play,
    resolveChildren: async () => {
      try {
        const items = await getAvailableTasks();
        return items.map((t) => ({
          id: `tasks.available.${t.id}`,
          parentId: "tasks.available",
          title: (t as unknown as { title?: string }).title || "Untitled task",
          icon: ListChecks,
          href: `/tasks#${t.id}`,
        }));
      } catch {
        return [];
      }
    },
  },
  {
    id: "tasks.overdue",
    parentId: "tasks",
    title: "Overdue",
    icon: Clock,
    resolveChildren: async () => {
      try {
        const items = await getOverdueTasks();
        return items.map((t) => ({
          id: `tasks.overdue.${t.id}`,
          parentId: "tasks.overdue",
          title: (t as unknown as { title?: string }).title || "Untitled task",
          icon: ListChecks,
          href: `/tasks#${t.id}`,
        }));
      } catch {
        return [];
      }
    },
  },
];

const taskVerbs: Verb[] = [
  { id: "tasks.open", title: "Open", icon: ListChecks, run: (e, ctx) => { ctx.router.push(`/tasks#${(e as { id: string }).id}`); } },
  { id: "tasks.start", title: "Start", icon: Play, run: () => {} },
  { id: "tasks.complete", title: "Mark complete", icon: CheckCircle, run: () => {} },
  { id: "tasks.skip", title: "Skip", icon: SkipForward, run: () => {} },
  { id: "tasks.dismiss", title: "Dismiss", icon: X, destructive: true,
    confirm: { title: "Dismiss this task?", body: "It will be removed from your list.", cta: "Dismiss" },
    run: () => {} },
];

const tasksProvider: SearchProvider = {
  id: "tasks",
  label: "Tasks",
  icon: ListChecks,
  prefix: "@tasks",
  async search(q, signal) {
    if (!q) return [];
    try {
      const res = await getTasks({ limit: 50 });
      if (signal.aborted) return [];
      const lower = q.toLowerCase();
      const items = (res.items as Array<{ id: string; title?: string; status?: string }>)
        .filter((t) => (t.title ?? "").toLowerCase().includes(lower))
        .slice(0, 6);
      return items.map<Result>((t) => ({
        id: `task:${t.id}`,
        title: t.title || "Untitled task",
        subtitle: t.status ?? undefined,
        icon: ListChecks,
        scopeChain: [TASKS_PARENT],
        score: 40,
        verbs: taskVerbs,
        entity: t,
        onSelect: (ctx) => ctx.push({
          kind: "entity",
          result: {
            id: `task:${t.id}`,
            title: t.title || "Untitled task",
            icon: ListChecks,
            scopeChain: [TASKS_PARENT],
            onSelect: () => {},
            verbs: taskVerbs,
            entity: t,
          },
        }),
      }));
    } catch {
      return [];
    }
  },
};

export const tasksPalette: FeaturePalette = {
  scopes,
  searchProviders: [tasksProvider],
};
